/**
 * Vista Mis Afiliados — jugadores captados por el promotor (es_origen de su
 * nodo), con su volumen jugado y GGR en el rango.
 *
 * Además del listado, es la herramienta de REPASO: agrupa a los jugadores por lo
 * que hicieron con su dinero (ver GRUPO_REPASO en _helpers.js) y permite abrir
 * WhatsApp con cada uno. Sin esto el promotor sabe cuántos recargaron, pero no
 * cuáles — y no puede hacer su trabajo de seguimiento.
 *
 * Consume GET /api/cashier/afiliados?desde=&hasta=.
 */

import { t } from '../languages.js';
import {
    rangoPorDefecto,
    montoDisplay,
    telefonoLegible,
    estadoAfiliado,
    GRUPO_REPASO,
    grupoRepasoDelAfiliado,
    diasDesdeUltimoRepaso,
    prioridadDeContacto,
    DIAS_DESCANSO_ENTRE_REPASOS,
} from './_helpers.js';
import { renderChipsFecha } from './chips-fecha.js';
import { contarEntero } from '../systems/motion.js';

// Orden de los filtros: primero los que más valor tienen para contactar.
// Cada grupo (salvo TODOS) trae su guía: quiénes son, en qué tono hablarles y
// un mensaje listo. Sin esto la lista es un reporte mudo — con esto el promotor
// aprende a repasar usándola, que es más efectivo que cualquier manual.
const FILTROS_REPASO = [
    { grupo: GRUPO_REPASO.TODOS, clave: 'afiliados.filtroTodos' },
    { grupo: GRUPO_REPASO.POR_COBRAR, clave: 'afiliados.filtroPorCobrar', guia: 'PorCobrar' },
    { grupo: GRUPO_REPASO.FIEL, clave: 'afiliados.filtroFieis', guia: 'Fiel' },
    { grupo: GRUPO_REPASO.INVIRTIO, clave: 'afiliados.filtroInvestiu', guia: 'Invirtio' },
    { grupo: GRUPO_REPASO.SOLO_BONO, clave: 'afiliados.filtroSoloBono', guia: 'SoloBono' },
];

function guiaDelGrupo(grupo) {
    const filtro = FILTROS_REPASO.find(f => f.grupo === grupo);
    if (!filtro || !filtro.guia) return null;
    return {
        quienes: t(`afiliados.guia${filtro.guia}Quienes`),
        tono: t(`afiliados.guia${filtro.guia}Tono`),
        mensaje: t(`afiliados.guia${filtro.guia}Mensaje`),
    };
}

// Estado del repaso junto al estado de actividad. "Volvió" es la prueba de que
// escribirle sirvió — es lo que hace que el promotor repita la conducta.
function etiquetaRepaso(afiliado) {
    const dias = diasDesdeUltimoRepaso(afiliado);
    if (dias === null) return '';
    if (afiliado.repaso_funciono) {
        return ` · <span class="pm-afi-repaso-ok">${t('afiliados.repasoVolvio')}</span>`;
    }
    const cuando = dias <= 0 ? t('afiliados.repasoHoy') : `${dias}d`;
    return ` · <span class="pm-afi-repaso">${t('afiliados.repasado')} ${cuando}</span>`;
}

// Fila de un afiliado — la usan la lista y la cola de prioridad (una sola
// versión del markup). El botón se atenúa si fue repasado hace menos del
// descanso y aún no volvió: dejarlo respirar antes de insistir.
function filaAfiliado(a) {
    const est = estadoAfiliado(a.dias_inactivo);
    const recargado = a.total_recargado || 0;
    const etiquetaMonto = recargado > 0 ? t('afiliados.recargado') : t('afiliados.volumen');
    const monto = recargado > 0 ? recargado : a.volumen_jugado;
    const diasRepaso = diasDesdeUltimoRepaso(a);
    const enDescanso = diasRepaso !== null && diasRepaso < DIAS_DESCANSO_ENTRE_REPASOS && !a.repaso_funciono;
    return `
                <div class="pm-afi-row">
                    <span class="pm-afi-estado" style="background:${est.color};"></span>
                    <div class="pm-afi-tel">
                        <div class="pm-afi-num">${telefonoLegible(a.telefono)}</div>
                        <div class="pm-afi-est-label">${est.label}${etiquetaRepaso(a)}</div>
                    </div>
                    <div class="pm-afi-montos">
                        <div class="pm-afi-vol">${montoDisplay(monto)}</div>
                        <div class="pm-afi-ggr-label">${etiquetaMonto}</div>
                    </div>
                    <button class="pm-afi-wa${enDescanso ? ' pm-afi-wa-descanso' : ''}"
                            data-tel="${(a.telefono || '').replace(/\D/g, '')}"
                            data-cliente="${a.cliente_id}"
                            aria-label="WhatsApp" ${a.telefono ? '' : 'disabled'}>${iconWhatsapp()}</button>
                </div>`;
}

// La cola corta obliga a elegir: 5 contactos hechos valen más que 50 mirados.
const TOPE_COLA_PRIORIDAD = 5;

function iconRayo() {
    return '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/></svg>';
}

function iconCopiar() {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
}

// Copia al portapapeles con degradación a prompt (navegadores viejos de gama baja).
async function copiarTexto(texto, boton, etiquetaOk) {
    try {
        await navigator.clipboard.writeText(texto);
    } catch (_) {
        window.prompt('', texto);
        return;
    }
    if (!boton) return;
    const original = boton.innerHTML;
    boton.innerHTML = `✓ ${etiquetaOk}`;
    setTimeout(() => {
        boton.innerHTML = original;
    }, 1500);
}

function iconWhatsapp() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5 0-.2 0-.4-.1-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1.1 2.8 1.2 3c.1.2 2.1 3.2 5 4.5.7.3 1.2.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.2-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.5A10 10 0 1 0 12 2z"/></svg>';
}

export function renderAfiliados({ apiGet, apiPost }) {
    const el = document.createElement('div');
    const { desde, hasta } = rangoPorDefecto();

    el.innerHTML = `
        <div class="card-hero" style="margin-bottom:2px;">
            <span class="hero-num" id="pm-afi-total">0</span>
            <div class="hero-label">${t('afiliados.totalAfiliados')}</div>
            <div class="pm-afi-marcador" id="pm-afi-marcador" hidden></div>
        </div>
        <div class="card">
            <h2>${t('afiliados.titulo')}</h2>
            <div class="date-range">
                <input type="date" id="pm-desde" value="${desde}" aria-label="${t('common.desde')}">
                <input type="date" id="pm-hasta" value="${hasta}" aria-label="${t('common.hasta')}">
            </div>
            <div class="pm-afi-filtros" id="pm-afi-filtros">
                ${FILTROS_REPASO.map(
                    f =>
                        `<button type="button" class="pm-afi-filtro${f.grupo === GRUPO_REPASO.TODOS ? ' active' : ''}" data-grupo="${f.grupo}">
                            ${t(f.clave)} <span class="pm-afi-filtro-n" data-conteo="${f.grupo}">0</span>
                        </button>`
                ).join('')}
            </div>
            <div id="pm-afiliados-body">
                <div style="text-align:center; color:var(--text-dim); padding:20px;">${t('common.cargando')}</div>
            </div>
        </div>`;

    const body = el.querySelector('#pm-afiliados-body');
    const desdeEl = el.querySelector('#pm-desde');
    const hastaEl = el.querySelector('#pm-hasta');
    const totalEl = el.querySelector('#pm-afi-total');
    const filtrosEl = el.querySelector('#pm-afi-filtros');

    let listaCompleta = [];
    let grupoActivo = GRUPO_REPASO.TODOS;

    // Chips de fecha encima de los inputs.
    const chips = renderChipsFecha({ desdeEl, hastaEl, onChange: () => cargar() });
    desdeEl.closest('.date-range').insertAdjacentElement('beforebegin', chips);

    let cargando = false;
    async function cargar() {
        if (cargando) return;
        cargando = true;
        body.innerHTML = `<div style="text-align:center; color:var(--text-dim); padding:24px;">${t('common.cargando')}</div>`;
        const r = await apiGet(`/api/cashier/afiliados?desde=${desdeEl.value}&hasta=${hastaEl.value}`);
        cargando = false;
        if (!r || r.ok === false) {
            body.innerHTML = `<div style="text-align:center; color:var(--danger); padding:24px;">${(r && r.mensaje) || t('common.reintentar')}</div>`;
            return;
        }
        listaCompleta = r.afiliados || [];
        // Count-up del total héroe.
        if (totalEl) contarEntero(totalEl, listaCompleta.length);
        pintarMarcador();
        pintarConteos();
        pintarLista();
    }

    // Marcador del promotor: su trabajo de repaso convertido en resultado.
    // "Volvieron" es lo que hace que repita la conducta — se lo mostramos arriba,
    // no enterrado en las filas. Oculto hasta el primer repaso (sin datos no hay
    // marcador que mostrar, y un "0 · 0" desanima antes de empezar).
    function pintarMarcador() {
        const marcadorEl = el.querySelector('#pm-afi-marcador');
        if (!marcadorEl) return;
        const repasados = listaCompleta.filter(a => a.ultimo_repaso).length;
        if (!repasados) {
            marcadorEl.hidden = true;
            return;
        }
        const volvieron = listaCompleta.filter(a => a.repaso_funciono).length;
        marcadorEl.hidden = false;
        marcadorEl.innerHTML =
            `${t('afiliados.marcadorRepasados')} <b>${repasados}</b>` +
            ` · <span class="pm-afi-repaso-ok">${t('afiliados.marcadorVolvieron')} <b>${volvieron}</b></span>`;
    }

    // Conteo por grupo en cada filtro: el promotor ve de un vistazo dónde tiene
    // trabajo pendiente sin necesidad de abrir cada pestaña.
    function pintarConteos() {
        FILTROS_REPASO.forEach(f => {
            const span = filtrosEl.querySelector(`[data-conteo="${f.grupo}"]`);
            if (!span) return;
            span.textContent =
                f.grupo === GRUPO_REPASO.TODOS
                    ? listaCompleta.length
                    : listaCompleta.filter(a => grupoRepasoDelAfiliado(a) === f.grupo).length;
        });
    }

    function listaVisible() {
        return grupoActivo === GRUPO_REPASO.TODOS
            ? listaCompleta
            : listaCompleta.filter(a => grupoRepasoDelAfiliado(a) === grupoActivo);
    }

    function pintarLista() {
        const lista = listaVisible();

        // Cabecera del grupo: qué lista es, en qué tono hablarles, el mensaje listo
        // para copiar y los números para armar una difusión (WhatsApp no permite
        // enviar a varios desde un enlace — la difusión la crea el promotor).
        const guia = guiaDelGrupo(grupoActivo);
        const cabecera = guia
            ? `<div class="pm-afi-guia">
                   <div class="pm-afi-guia-quienes">${guia.quienes}</div>
                   <div class="pm-afi-guia-tono">${guia.tono}</div>
                   <div class="pm-afi-guia-msg" id="pm-afi-msg-sugerido">${guia.mensaje}</div>
                   <div class="pm-afi-guia-acciones">
                       <button type="button" class="pm-afi-guia-btn" id="pm-afi-copiar-msg">${iconCopiar()} ${t('afiliados.copiarMensaje')}</button>
                       <button type="button" class="pm-afi-guia-btn" id="pm-afi-copiar-nums">${iconCopiar()} ${t('afiliados.copiarNumeros')}</button>
                   </div>
               </div>`
            : '';

        if (lista.length === 0) {
            body.innerHTML = `${cabecera}<div style="text-align:center; color:var(--text-dim); padding:20px; font-size:15px;">${t('common.sinDatos')}</div>`;
            return;
        }
        // Lista en CARDS premium (no tabla apretada) — cada afiliado una fila grande.
        // El monto que se muestra es el RECARGADO (dinero propio del jugador), no el
        // volumen jugado: es lo que distingue al cliente real del que gastó el bono.
        body.innerHTML = colaDePrioridadHtml() + cabecera + lista.map(filaAfiliado).join('');
    }

    // Cola de prioridad: los contactos que más rinden HOY, cruzando valor del
    // jugador con urgencia de la ventana (la probabilidad de recuperar cae 27%→2%
    // en 90 días — el tiempo manda). Solo en la pestaña "Todos": dentro de un
    // grupo el promotor ya eligió a quién mirar.
    function colaDePrioridadHtml() {
        if (grupoActivo !== GRUPO_REPASO.TODOS) return '';
        const top = listaCompleta
            .map(a => ({ a, score: prioridadDeContacto(a) }))
            .filter(x => x.score > 0 && x.a.telefono)
            .sort((x, y) => y.score - x.score || (x.a.dias_inactivo || 99) - (y.a.dias_inactivo || 99))
            .slice(0, TOPE_COLA_PRIORIDAD)
            .map(x => x.a);
        if (!top.length) return '';
        return `
            <div class="pm-afi-prioridad">
                <div class="pm-afi-prioridad-titulo">${iconRayo()} ${t('afiliados.prioridadHoy')}</div>
                ${top.map(filaAfiliado).join('')}
            </div>`;
    }

    // Copiar el mensaje sugerido, o todos los números del grupo. Lo segundo es
    // para armar una lista de difusión en el teléfono: WhatsApp no permite
    // enviar a varios desde un enlace, así que el envío masivo lo hace el
    // promotor desde su app con los números ya pegados.
    body.addEventListener('click', e => {
        const btnMsg = e.target.closest('#pm-afi-copiar-msg');
        if (btnMsg) {
            const guia = guiaDelGrupo(grupoActivo);
            if (guia) copiarTexto(guia.mensaje, btnMsg, t('afiliados.mensajeCopiado'));
            return;
        }
        const btnNums = e.target.closest('#pm-afi-copiar-nums');
        if (btnNums) {
            const numeros = listaVisible()
                .map(a => a.telefono)
                .filter(Boolean)
                .join('\n');
            if (numeros) copiarTexto(numeros, btnNums, t('afiliados.numerosCopiados'));
        }
    });

    // Cambio de filtro: solo re-pinta (los datos ya están en memoria).
    filtrosEl.addEventListener('click', e => {
        const btn = e.target.closest('[data-grupo]');
        if (!btn) return;
        grupoActivo = btn.dataset.grupo;
        filtrosEl.querySelectorAll('.pm-afi-filtro').forEach(b => b.classList.toggle('active', b === btn));
        pintarLista();
    });

    // Contactar: registra el repaso y abre WhatsApp con el mensaje del grupo ya
    // puesto (el promotor lo edita antes de enviar si quiere). El registro va
    // ANTES de abrir: si esperáramos al retorno, en móvil la pestaña puede no
    // volver nunca y perderíamos el rastro del contacto.
    body.addEventListener('click', async e => {
        const btn = e.target.closest('.pm-afi-wa');
        if (!btn || !btn.dataset.tel || btn.disabled) return;

        // Doble-tap real en gama baja: sin este guard se registran dos repasos.
        btn.disabled = true;
        const clienteId = parseInt(btn.dataset.cliente, 10);
        const grupo = grupoRepasoDelAfiliado(listaCompleta.find(a => a.cliente_id === clienteId) || {});

        let repasoId = null;
        try {
            const r = await apiPost('/api/cashier/repasos', { clienteId, grupoRepaso: grupo });
            if (r && r.ok) repasoId = r.repasoId;
        } catch (_) {
            // El registro es secundario: si falla, el promotor igual debe poder
            // escribirle. Se pierde la métrica de ese contacto, no la acción.
        } finally {
            btn.disabled = false;
        }

        const guia = guiaDelGrupo(grupo);
        const texto = guia ? `?text=${encodeURIComponent(guia.mensaje)}` : '';
        window.open(`https://wa.me/${btn.dataset.tel}${texto}`, '_blank', 'noopener');

        if (repasoId) pedirConfirmacionDeEnvio(repasoId, clienteId);
    });

    // Al volver de WhatsApp preguntamos si llegó a enviar. Es una declaración del
    // promotor, no un hecho medible — la efectividad real se calcula con la
    // actividad posterior del jugador, no con esta respuesta.
    function pedirConfirmacionDeEnvio(repasoId, clienteId) {
        const afiliado = listaCompleta.find(a => a.cliente_id === clienteId);
        const barra = document.createElement('div');
        barra.className = 'pm-afi-confirmar';
        barra.innerHTML = `
            <span>${t('afiliados.confirmarEnvio')} ${afiliado ? telefonoLegible(afiliado.telefono) : ''}</span>
            <div class="pm-afi-confirmar-btns">
                <button type="button" data-envio="1">${t('afiliados.confirmarSi')}</button>
                <button type="button" data-envio="0">${t('afiliados.confirmarNo')}</button>
            </div>`;

        function cerrar() {
            barra.classList.remove('open');
            setTimeout(() => barra.remove(), 200);
        }
        barra.addEventListener('click', async ev => {
            const b = ev.target.closest('[data-envio]');
            if (!b) return;
            cerrar();
            try {
                await apiPost(`/api/cashier/repasos/${repasoId}/confirmar`, {
                    envioConfirmado: b.dataset.envio === '1',
                });
            } catch (_) {
                // Secundario: la confirmación no bloquea nada.
            }
            if (b.dataset.envio === '1') cargar();
        });

        document.body.appendChild(barra);
        requestAnimationFrame(() => barra.classList.add('open'));
        // Si el promotor la ignora, se va sola: no debe estorbar la lista.
        setTimeout(cerrar, 20000);
    }

    desdeEl.addEventListener('change', cargar);
    hastaEl.addEventListener('change', cargar);
    cargar();

    return el;
}
